import axios from "axios"
const baseUrl = process.env.REACT_APP_API_BASEURL

// Default fallback image URL - using an existing image from assets
const FALLBACK_IMAGE_URL = '/assets/images/check.png';

// Retry configuration
const RETRY_CONFIG = {
    maxRetries: 3,
    retryDelay: 1000, // 1 second
    retryableStatusCodes: [408, 429, 500, 502, 503, 504]
};

/**
 * @function sleep
 * @param {number} ms - Milliseconds to sleep
 * @returns {Promise} - Promise that resolves after the specified time
 */
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * @function isRetryableError
 * @param {Error} error - The error to check
 * @returns {boolean} - Whether the error is retryable
 */
const isRetryableError = (error) => {
    const status = error.response?.status;
    // Don't retry 4xx client errors (400, 401, 403, 404, etc.)
    if (status >= 400 && status < 500) {
        return false;
    }
    return RETRY_CONFIG.retryableStatusCodes.includes(status) || !status;
};

/**
 * @function retryWithBackoff
 * @param {Function} fn - The function to retry
 * @param {number} retriesLeft - Number of retries remaining
 * @returns {Promise} - The result of the function
 */
const retryWithBackoff = async (fn, retriesLeft = RETRY_CONFIG.maxRetries) => {
    try {
        return await fn();
    } catch (error) {
        if (retriesLeft === 0 || !isRetryableError(error)) {
            throw error;
        }
        
        console.log(`Retrying request (${RETRY_CONFIG.maxRetries - retriesLeft + 1}/${RETRY_CONFIG.maxRetries})...`);
        await sleep(RETRY_CONFIG.retryDelay * (RETRY_CONFIG.maxRetries - retriesLeft));
        return retryWithBackoff(fn, retriesLeft - 1);
    }
};

export const indicatorListService = async ({ authToken, filter, limit, offset, submissionId }) => {
    const result = await axios.get(`${baseUrl}indicator?offset=${offset}&limit=${limit}&filter=${filter}&submissionId=${submissionId}`, {
        headers: {
            Authorization: `Bearer ${authToken}`
        }
    })
    return result;
}

/**
 * @function validateFilePath
 * @param {Object} file - The file object containing path information
 * @returns {boolean} - Whether the file path is valid
 */
const validateFilePath = (file) => {
    if (!file || !file.path || !file.filename) {
        console.error('Invalid file object:', file);
        return false;
    }
    
    // Check if path starts with 'files/' and contains valid characters
    const validPath = /^files\/[a-zA-Z0-9-_.]+$/.test(file.path);
    if (!validPath) {
        console.error('Invalid file path format:', file.path);
        return false;
    }
    
    return true;
}

/**
 * @function getImageIndicatorService
 * @param (token, file)
 * @description call API to get the indicator image with retry logic and fallback
 */
export const getImageIndicatorService = async ({ token, file }) => {
    // Validate file object exists
    if (!file) {
        return Promise.reject(new Error('File object is required'));
    }

    console.log('Request payload:', {
        file: {
            ...file
        }
    });
    
    try {
        const result = await retryWithBackoff(async () => {
            const response = await axios.post(`${baseUrl}indicator/file`, {
                file: {
                    ...file
                }
            }, {
                responseType: 'arraybuffer',
                headers: {
                    Authorization: `Bearer ${token}`,
                    Accept: 'application/json'
                }
            });
            
            if (!response.data || response.data.byteLength === 0) {
                throw new Error('Empty response received');
            }
            
            return response;
        });

        return result;
    } catch (error) {
        // Try to decode ArrayBuffer error response
        let errorMessage = 'Unknown error';
        if (error.response?.data instanceof ArrayBuffer) {
            try {
                const decoder = new TextDecoder('utf-8');
                errorMessage = decoder.decode(error.response.data);
            } catch (e) {
                errorMessage = 'Failed to decode error response';
            }
        } else if (typeof error.response?.data === 'string') {
            errorMessage = error.response.data;
        } else if (error.response?.data?.message) {
            errorMessage = error.response.data.message;
        }
        
        // Check if this is a file not found error (ENOENT) - this is expected
        const isFileNotFound = errorMessage.includes('ENOENT') || 
                               errorMessage.includes('no such file') ||
                               error.response?.status === 404;
        
        // Only log unexpected errors (not 404s or file not found errors)
        if (!isFileNotFound && error.response?.status !== 404) {
            console.error('Failed to load image after retries:', {
                status: error.response?.status,
                statusText: error.response?.statusText,
                errorMessage: errorMessage,
                url: error.config?.url,
                method: error.config?.method
            });
        }

        // Return fallback image data
        try {
            const fallbackResponse = await axios.get(FALLBACK_IMAGE_URL, {
                responseType: 'arraybuffer'
            });
            return {
                data: fallbackResponse.data,
                headers: {
                    'content-type': 'image/png'
                }
            };
        } catch (fallbackError) {
            // Only log if fallback also fails
            if (fallbackError.response?.status !== 404) {
            console.error('Failed to load fallback image:', fallbackError);
            }
            // Return a minimal error that won't break the UI
            throw new Error('Image not available');
        }
    }
}

export const activeIndicatorListService = ({ token }) => axios.get(`${baseUrl}indicator?filter=Active`, {
    headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json'
    }
})